/* eslint-disable max-lines */
/* eslint-disable no-param-reassign */
import { api } from "jiffy-api";
import { getFormFieldMapping } from "./address-fields";

/**
 * AddressServiceValidator
 *
 * Handles address validation with external services including:
 * - ZIP code to state lookup
 * - Address validation via API
 * - Error processing and field mapping
 *
 * @example
 * const validator = new AddressServiceValidator();
 * const result = await validator.validateAddress({ formData, countryId, errors, errorMessage, userId });
 */
class AddressServiceValidator {
  static ERROR_MESSAGES = {
    ADDRESS_VALIDATION_FAILED:
      "The address you entered could not be validated. Please click 'Use this address' to proceed with this address, or enter a valid address.",
    ADDRESS_VALIDATION_ERROR:
      "Unable to validate address. Please check your information and try again.",
    INVALID_ZIP_CODE: "Invalid ZIP code.",
    ZIP_CODE_VALIDATION_ERROR:
      "Unable to validate ZIP code. Please check your information and try again."
  };

  static #FIELD_MAPPINGS = getFormFieldMapping();

  /**
   * Creates a new AddressServiceValidator instance
   * @public
   */
  constructor() {
    this.clearCache();
  }

  /**
   * Processes validation errors from the address service response.
   * Maps known fields to form field names, and collects unmapped errors for general display.
   *
   * @param {Object} responseErrors - Error response from the server (may have a messages property or be the errors object directly)
   * @param {Object} errors - Form errors object to populate with field-specific errors
   * @param {Object} $errorMessageRef - Reference object for general error message display
   * @public
   * @static
   */
  static processValidationErrors(responseErrors, errors, $errorMessageRef) {
    const errorMessages = [];
    // Handle different response formats - some APIs nest errors under 'messages'
    const errorsToProcess = responseErrors.messages || responseErrors;

    Object.keys(errorsToProcess).forEach(field => {
      const messages = errorsToProcess[field];
      if (Array.isArray(messages) && messages.length > 0) {
        // Only display the first error per field to keep UI clean
        const [firstMessage] = messages;

        // Map backend field names to frontend form field names
        if (AddressServiceValidator.#FIELD_MAPPINGS[field]) {
          errors[AddressServiceValidator.#FIELD_MAPPINGS[field]] = firstMessage;
        } else {
          // Collect unmapped errors to display as general error message
          errorMessages.push(firstMessage);
        }
      }
    });

    // Display all unmapped errors together as a general error message
    if (errorMessages.length > 0) {
      $errorMessageRef.textContent = errorMessages.join("<br />");
    }
  }

  /**
   * Validates address using the API
   * @param {Object} options - Validation options
   * @param {Object} options.formData - Form data containing address information
   * @param {number} options.countryId - Country ID
   * @param {Object} options.errors - Form errors object
   * @param {Element} options.errorMessageElement - Error message DOM element
   * @param {number} options.userId - User ID
   * @returns {Promise<{success: boolean, errorMessage?: string, twoFactorNeeded?: boolean}>}
   * @public
   */
  async validateAddress({ formData, countryId, errors, errorMessageElement, userId }) {
    if (formData.customAddress) {
      return {
        success: true,
        twoFactorNeeded: this.cachedValidationResponse?.two_factor_needed || false
      };
    }

    try {
      const address = AddressServiceValidator.buildAddressPayload(formData, countryId, userId);
      const response = await api.jiffyshirts.validateAddress({
        address,
        business_line_path: "print"
      });

      this.cachedValidationResponse = response;

      if (response.errors && Object.keys(response.errors).length > 0) {
        AddressServiceValidator.processValidationErrors(
          response.errors,
          errors,
          errorMessageElement
        );
      }

      if (AddressServiceValidator.shouldAllowOverride(response, formData)) {
        return {
          success: false,
          errorMessage: AddressServiceValidator.ERROR_MESSAGES.ADDRESS_VALIDATION_FAILED,
          twoFactorNeeded: response.two_factor_needed
        };
      }

      return {
        success: true,
        twoFactorNeeded: response.two_factor_needed
      };
    } catch (error) {
      return {
        success: false,
        errorMessage: AddressServiceValidator.ERROR_MESSAGES.ADDRESS_VALIDATION_ERROR
      };
    }
  }

  /**
   * Builds address payload for validation API
   * @param {Object} formData - Form data
   * @param {number} countryId - Country ID
   * @param {number} userId - User ID
   * @returns {Object} Address payload
   * @private
   * @static
   */
  static buildAddressPayload(formData, countryId, userId) {
    return {
      fullname: formData.fullName.trim(),
      address1: formData.address1.trim(),
      address2: formData.address2.trim(),
      zipcode: formData.zipcode.trim(),
      city: formData.city.trim(),
      state_id: formData.stateId,
      country_id: countryId,
      phone: formData.phone.trim(),
      user_id: userId
    };
  }

  /**
   * Determines if override should be allowed based on validation response
   * @param {Object} response - Validation API response
   * @param {Object} formData - Form data
   * @returns {boolean}
   * @private
   * @static
   */
  static shouldAllowOverride(response, formData) {
    return (
      response.allow_override ||
      (response.address.zipcode !== formData.zipcode.trim() && formData.customAddress === false)
    );
  }

  /**
   * Clears cached validation response
   * @public
   */
  clearCache() {
    this.cachedValidationResponse = null;
  }
}

export default AddressServiceValidator;
